//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useCallback, useEffect, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { useHistory, useParams } from 'react-router-dom';
import Backdrop from '@material-ui/core/Backdrop';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { PATH_DASHBOARD } from '../system/constants';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import {
    DASHBOARD_MY_PROFILE,
    DASHBOARD_MY_CALENDAR,
    DASHBOARD_MY_JOBS,
    DASHBOARD_MY_SETTINGS,
    DASHBOARD_MY_MESSAGES,
    DASHBOARD_MY_ANALYTICS,
    DASHBOARD_ID_MY_SETTINGS,
    DASHBOARD_ID_MY_PROFILE,
    DASHBOARD_ID_MY_JOBS,
    DASHBOARD_ID_MY_MESSAGES,
    DASHBOARD_ID_MY_CALENDAR,
} from '../common/constants';
import { BASE_URL } from '../common/constants';
import { handleCloseSnackbar } from '../common/snackbarHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdSnackbar from '../common/elemental/AfitdSnackbar';
import TopNav from '../topNav/TopNav';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import LeftNavigation from './subComponents/LeftNavigation';
// import SamplePage from './SamplePage';
import MyProfile from '../dashboardMyProfile';
import MyCalendar from '../dashboardMyCalendar';
import MyJobs from '../dashboardMyJobs';
import MySettings from '../dashboardMySettings';
import MyMessages from '../dashboardMyMessages';
import MyAnalytics from './SamplePage'; //'./dashboardMyAnalytics';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    dashboardContainer: { overflow: 'hidden', height: '100%', minHeight: '100%', flexWrap: 'unset' },
    dashboardHeader: { maxHeight: '81px' },
    dashboardContent: { flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: '64px', [theme.breakpoints.down('md')]: { paddingRight: '0px', justifyContent: 'center' } },
    dashboardLeft: {},
    dashboardRight: { height: '100%', [theme.breakpoints.down('lg')]: { height: '735px' }, [theme.breakpoints.down('md')]: { height: '100%' } },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { /*props,*/ classes, snackbarMessage, setSnackbarMessage, snackbarErrorMessage, setSnackbarErrorMessage } = params;
    // const {} = props;

    let defns = {
        dashboardContainer: { container: true, direction: 'column' },
        dashboardHeader: { item: true, lg: 12, md: 12, sm: 12, xs: 12 },
        dashboardContent: { item: true, container: true },
        dashboardLeft: { item: true, lg: 3, md: 0, sm: 0, xs: 0 },
        dashboardRight: { item: true, lg: 9, md: 10, sm: 11, xs: 11 },

        snackbar: {
            message: { successMessage: snackbarMessage, errorMessage: snackbarErrorMessage },
            onClose: handleCloseSnackbar.bind(null, setSnackbarMessage, setSnackbarErrorMessage),
        },
        leftNavigation: {},
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const getLayoutComponent = (dashboard, history) => {
    switch (dashboard) {
        case DASHBOARD_MY_PROFILE:
            return <MyProfile />;
        case DASHBOARD_MY_JOBS:
            return <MyJobs />;
        // history.push(`${BASE_URL}/my-jobs-figma-converted`);
        // return true ? null : <MyJobs />;
        case DASHBOARD_MY_SETTINGS:
            return <MySettings />;
        case DASHBOARD_MY_MESSAGES:
            return <MyMessages />;
        case DASHBOARD_MY_ANALYTICS:
            history.push(`${BASE_URL}/my-analytics`);
            return true ? null : <MyAnalytics />;
        case DASHBOARD_MY_CALENDAR:
            return <MyCalendar />;
        default:
            return <MyCalendar />;
    }
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const DashboardLayout = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const layoutContext = useContext(LayoutContext);
    const userId = _get(appContext, 'authUser.uid');
    const pendingAccountSetup = _get(appContext, 'pendingAccountSetup');
    const classes = useStyles();
    const { pageId } = useParams();
    // const defaultPage = pageId === 1 || pendingAccountSetup ? DASHBOARD_MY_SETTINGS : DASHBOARD_MY_CALENDAR;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [dashboard, setDashboard] = useState();
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState();
    const [currentThreadId, setCurrentThreadId] = useState();
    const [asyncCallCount, setAsyncCallCount] = React.useState(0);

    const [tempUpdatedJobNotes, setTempUpdatedJobNotes] = React.useState({});

    const [mainBackdropOpen, setMainBackdropOpen] = useState(false);

    //---------------------------------------------------------------------
    // TEMP TEMP TEMP TEMP TEMP TEMP
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    //---------------------------------------------------------------------
    const history = useHistory();
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const setCurrentDashboard = useCallback(
        newPageId => {
            // setDashboard(newDashboard);
            history.push(`${BASE_URL}${PATH_DASHBOARD}/${newPageId}`);
        },
        [history],
    );

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setCurrentThreadId(null);
    }, [dashboard]);

    useEffect(() => {
        switch (pageId) {
            case DASHBOARD_ID_MY_SETTINGS:
                setDashboard(DASHBOARD_MY_SETTINGS);
                break;
            case DASHBOARD_ID_MY_PROFILE:
                setDashboard(DASHBOARD_MY_PROFILE);
                break;
            case DASHBOARD_ID_MY_JOBS:
                setDashboard(DASHBOARD_MY_JOBS);
                break;
            case DASHBOARD_ID_MY_MESSAGES:
                setDashboard(DASHBOARD_MY_MESSAGES);
                break;
            case DASHBOARD_ID_MY_CALENDAR:
            default: {
                if (pendingAccountSetup) {
                    setDashboard(DASHBOARD_MY_SETTINGS);
                } else {
                    setDashboard(DASHBOARD_MY_CALENDAR);
                }
            }
        }
    }, [pageId, pendingAccountSetup]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ props, classes, snackbarMessage, setSnackbarMessage, snackbarErrorMessage, setSnackbarErrorMessage });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return !dashboard ? null : (
        <LayoutContext.Provider
            value={{
                ...layoutContext,
                userId,
                dashboard,
                tempSetDashboard: setDashboard,
                setCurrentDashboard,
                setSnackbarMessage,
                setSnackbarErrorMessage,
                currentThreadId,
                setCurrentThreadId,
                setAsyncCallCount,
                tempUpdatedJobNotes,
                setTempUpdatedJobNotes,
                setMainBackdropOpen,
            }}
        >
            <Grid {...defns.dashboardContainer}>
                <Grid {...defns.dashboardHeader}>
                    <TopNav isShowProgressBar={asyncCallCount > 0} />
                </Grid>
                <Grid {...defns.dashboardContent}>
                    <Grid {...defns.dashboardLeft}>
                        <LeftNavigation {...defns.leftNavigation} />
                    </Grid>
                    <Grid {...defns.dashboardRight}>{getLayoutComponent(dashboard, history)}</Grid>
                </Grid>
            </Grid>
            <Backdrop open={mainBackdropOpen} style={{ zIndex: 100 }} />
            <AfitdSnackbar {...defns.snackbar} />
        </LayoutContext.Provider>
    );
};

export default DashboardLayout;
